import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Shield, FileText, AlertTriangle, CheckCircle2, Activity, ClipboardCheck,
  TrendingUp, Sparkles, ArrowRight, ShieldAlert, ClipboardList, Scale, FileSignature,
} from 'lucide-react';
import Header from '../components/Header';
import { SECTIONS } from '../lib/sectionTheme';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const C = SECTIONS.compliance;

export default function CompliancePage() {
  const { user } = useAuth();
  const isPM = user?.role === 'admin' || user?.role === 'superuser';

  const [governance, setGovernance] = useState(null);
  const [sopStats, setSopStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const reqs = [
      axios.get(`${API}/sops/stats`, { withCredentials: true }).then(r => setSopStats(r.data)).catch(() => {}),
    ];
    if (isPM) {
      reqs.push(
        axios.get(`${API}/sops-governance/overview`, { withCredentials: true }).then(r => setGovernance(r.data)).catch(() => {})
      );
    }
    Promise.all(reqs).finally(() => setLoading(false));
  }, [isPM]);

  return (
    <div className="min-h-screen bg-background" data-testid="compliance-page">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4" style={{ color: C.color }} />
            <span className="text-xs font-bold uppercase tracking-[0.18em]" style={{ color: C.color }}>Compliance hub</span>
          </div>
          <h1 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
            Practice <span style={{ color: C.color }}>compliance</span>
          </h1>
          <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-2xl">
            A rolled-up view of governance, audits, infection control, radiation safety and incident reporting. Every card links to the full detail view.
          </p>
        </div>

        {/* Top KPI row */}
        {sopStats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="compliance-kpis">
            <KPI icon={TrendingUp} label="SOP compliance"
              value={`${sopStats.compliance_rate}%`}
              color={sopStats.compliance_rate >= 80 ? '#16A34A' : sopStats.compliance_rate >= 50 ? '#F59E0B' : '#EF4444'} />
            <KPI icon={ClipboardCheck} label="Active SOPs" value={sopStats.total_sops} color={SECTIONS.sops.color} />
            <KPI icon={CheckCircle2} label="Completed tasks" value={sopStats.completed} color="#16A34A" />
            <KPI icon={AlertTriangle} label="Overdue SOP tasks" value={sopStats.overdue}
              color={sopStats.overdue > 0 ? '#EF4444' : '#64748B'} />
          </div>
        )}

        {/* Governance review row (PM only) */}
        {isPM && governance && (
          <div className="bg-card border border-border rounded-2xl p-6 mb-8" data-testid="compliance-governance">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
              <div>
                <h2 className="font-['Outfit'] text-lg font-semibold text-foreground flex items-center gap-2">
                  <Shield className="w-4 h-4" style={{ color: C.color }} />
                  Governance — SOP review cycle
                </h2>
                <p className="text-xs text-muted-foreground mt-0.5">12-month review anchor. Jump into the full governance dashboard for sign-off.</p>
              </div>
              <Link to="/sops/governance" data-testid="compliance-governance-link"
                className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg border"
                style={{ borderColor: `${C.color}40`, color: C.color }}>
                Open dashboard <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <StatTile label="Overdue" value={governance.summary?.overdue || 0} color="#EF4444" />
              <StatTile label="Due soon" value={governance.summary?.due_soon || 0} color="#F59E0B" />
              <StatTile label="Current" value={governance.summary?.current || 0} color="#16A34A" />
            </div>
          </div>
        )}

        {/* Live module cards */}
        <div className="mb-4">
          <h2 className="font-['Outfit'] text-lg font-semibold text-foreground">Compliance modules</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Each card opens the module's live records and audit trail.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10" data-testid="compliance-modules">
          <ModuleCard to="/sops" icon={ClipboardCheck} title="Standard Operating Procedures"
            desc="All seeded + custom SOPs, per-staff assignments, step-by-step walkthrough"
            color={SECTIONS.sops.color} testid="compliance-sops-card" />
          <ModuleCard to="/sops/governance" icon={Shield} title="SOP governance"
            desc="12-month review cycle, sign-off register, ISO alignment"
            color="#7C3AED" adminOnly isPM={isPM} testid="compliance-governance-card" />
          <ModuleCard to="/ipc" icon={ClipboardList} title="Infection Prevention & Control"
            desc="Daily cleaning log, antiseptic bottle register, monthly audit"
            color="#DC2626" testid="compliance-ipc-card" />
          <ModuleCard to="/preparation" icon={FileText} title="Patient preparation library"
            desc="Prep notes for every exam and special patient groups"
            color={SECTIONS.preparation.color} testid="compliance-prep-card" />
          <ModuleCard to="/screening" icon={FileText} title="Patient screening forms"
            desc="RANZCR contrast, blood-thinner, pregnancy, consent"
            color="#0891B2" testid="compliance-screening-card" />
          <ModuleCard to="/consent" icon={FileSignature} title="Image-guided procedure consent"
            desc="Montgomery-standard consent for spinal injections, biopsy, PRP, FNA"
            color="#7C2D12" testid="compliance-consent-card" />
          <ModuleCard to="/incidents" icon={AlertTriangle} title="Incident reporting"
            desc="Report near misses, patient safety events, complaints, equipment + WHS"
            color="#DC2626" testid="compliance-incidents-card" />
          <ModuleCard to="/dashboard/logins" icon={ShieldAlert} title="Login activity & audit"
            desc="Every auth event with IP, device, alerts for suspicious activity"
            color="#EF4444" adminOnly isPM={isPM} testid="compliance-logins-card" />
          <ModuleCard to="/disputes" icon={Scale} title="Conflict & dispute register"
            desc="Staff complaints, mediation outcomes, audit trail"
            color="#7C3AED" adminOnly isPM={isPM} testid="compliance-disputes-card" />
          <ModuleCard to="/mbs" icon={Activity} title="MBS fee compliance"
            desc="Quarterly MBS fee-schedule change tracker"
            color={SECTIONS.mbs.color} testid="compliance-mbs-card" />
        </div>

        {/* Framework alignment — aspirational, read-only */}
        <div className="bg-card border border-border rounded-2xl p-6" data-testid="compliance-frameworks">
          <h2 className="font-['Outfit'] text-lg font-semibold text-foreground flex items-center gap-2 mb-3">
            <Shield className="w-4 h-4" style={{ color: C.color }} />
            Framework alignment
          </h2>
          <p className="text-xs text-muted-foreground mb-4">
            The app is built around the following accreditation and regulatory frameworks. Upload evidence PDFs directly to each module's audit trail.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Framework title="DIAS — Diagnostic Imaging Accreditation Scheme"
              items={['Std 1 Governance', 'Std 2 Patient ID & Safety', 'Std 4 Medication Safety', 'Std 5 Infection Control', 'Std 6 Incident Management']} />
            <Framework title="ISO 15189 — Medical Laboratories"
              items={['§4.14 Incident management', 'Quality manual alignment', 'Internal audit cycle']} />
            <Framework title="RANZCR"
              items={['Iodinated Contrast Media Guideline V2.3', 'Gadolinium Guidance', 'Urgent & Critical Findings communication']} />
            <Framework title="Work Health & Safety"
              items={['WHS Act 2011', 'Zero-tolerance policy', 'Staff de-escalation training']} />
          </div>
        </div>

        {loading && <p className="mt-6 text-center text-xs text-muted-foreground">Loading live data…</p>}
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-card border border-border rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
      </div>
      <p className="font-['Outfit'] text-3xl font-bold text-foreground">{value}</p>
    </div>
  );
}

function StatTile({ label, value, color }) {
  return (
    <div className="text-center p-4 rounded-xl border" style={{ borderColor: `${color}40`, backgroundColor: `${color}08` }}>
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="font-['Outfit'] text-3xl font-bold mt-1" style={{ color }}>{value}</p>
    </div>
  );
}

function ModuleCard({ to, icon: Icon, title, desc, color, adminOnly, isPM, testid }) {
  if (adminOnly && !isPM) return null;
  return (
    <Link to={to} data-testid={testid}
      className="group relative overflow-hidden bg-card border border-border rounded-2xl p-5 hover:shadow-sm transition-all"
      style={{ borderLeftWidth: 3, borderLeftColor: color }}>
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
        <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:translate-x-1 transition-transform" />
      </div>
      <h3 className="font-['Outfit'] text-sm font-semibold text-foreground">{title}</h3>
      <p className="text-xs text-muted-foreground mt-1">{desc}</p>
    </Link>
  );
}

function Framework({ title, items }) {
  return (
    <div className="p-4 rounded-xl bg-muted/30 border border-border/60">
      <p className="text-sm font-semibold text-foreground mb-2">{title}</p>
      <ul className="space-y-1 text-xs text-muted-foreground">
        {items.map(i => <li key={i} className="flex items-start gap-1.5"><CheckCircle2 className="w-3 h-3 text-green-500 mt-0.5 shrink-0" /> {i}</li>)}
      </ul>
    </div>
  );
}
